import type { PermissionModule } from '~/types/permission'
import { useRoles } from '~/composables/useRoles'
import { isAgencyAdminAllowed, isAgencyHomeNavVisible, isAgencySuspendedRouteAllowed, isFiscalRouteBlockedForAgencyMember } from '~/utils/agencyRoutes'
import { useCollectionContext } from '~/composables/useCollectionContext'

/** Mapa: prefijo de ruta → módulo de permiso requerido (solo lectura) */
const ROUTE_MODULE_MAP: { prefix: string; module: PermissionModule }[] = [
  { prefix: '/patients',      module: 'patients' },
  { prefix: '/pregnancies',   module: 'patients' },
  { prefix: '/maternal-fetal', module: 'patients' },
  { prefix: '/agenda',        module: 'appointments' },
  { prefix: '/messages',      module: 'appointments' },
  { prefix: '/ehr',           module: 'ehr' },
  { prefix: '/expedientes',   module: 'ehr' },
  { prefix: '/surgeries',     module: 'surgeries' },
  { prefix: '/prescriptions', module: 'prescriptions' },
  { prefix: '/payments',      module: 'payments' },
{ prefix: '/gastos',        module: 'expenses' },
  { prefix: '/inventory',     module: 'inventory' },
  { prefix: '/vacunas/pendientes', module: 'vaccines_pending' },
  { prefix: '/procedures',    module: 'procedures' },
  { prefix: '/billing',       module: 'billing' },
  { prefix: '/bulletins',      module: 'bulletins' },
  { prefix: '/solicitudes',    module: 'appointment_requests' },
  { prefix: '/recordatorios',  module: 'appointments' },
  { prefix: '/triaje',         module: 'appointments' },
  { prefix: '/consultas',      module: 'consultation_billing' },
  { prefix: '/lab-orders',     module: 'prescriptions' },
  { prefix: '/settings',       module: 'settings' },
  { prefix: '/dashboard',     module: 'dashboard' },
  { prefix: '/informes',      module: 'informes' },
  { prefix: '/reproduccion',  module: 'assisted_reproduction' },
  { prefix: '/pos',           module: 'pos' },
  { prefix: '/sucursales',    module: 'sucursales' },
  { prefix: '/espacios',         module: 'spaces' },
  { prefix: '/medicos-externos', module: 'external_doctors' },
  { prefix: '/cuentas-medicos',  module: 'doctor_accounts' },
  { prefix: '/cuentas-pacientes', module: 'patient_accounts' },
  { prefix: '/cobranza',          module: 'collections' },
  { prefix: '/laboratorio',      module: 'laboratorio' },
]

/**
 * Rutas públicas que no requieren sesión ni permisos.
 * OJO con la barra final: `'/lab/'` cubre el informe público de laboratorio
 * (`pages/lab/[token].vue`) SIN abrir `/laboratorio` ni `/lab-orders`, que son
 * rutas internas con permiso de módulo (`startsWith('/lab/')` no matchea
 * `/laboratorio` ni `/lab-orders/...`).
 */
const PUBLIC_PREFIXES = ['/auth/', '/rx/', '/preop/', '/intake/', '/c/', '/shared/', '/factura/', '/autofactura/', '/pago/', '/privacy-policy', '/surgeries/public/', '/lab-orders/public/', '/lab-orders/upload/', '/lab/', '/mi-embarazo/', '/consentimiento-anestesia/', '/informes/r/', '/evaluacion/', '/plan-alimentacion/', '/referir/', '/cobro/', '/registro/']

/** Sub-rutas de settings accesibles a todos los usuarios autenticados */
const ALL_USER_SETTINGS = ['/settings/appearance', '/settings/profile']

/** Sub-rutas de settings accesibles a doctores (configuración personal) */
const DOCTOR_SETTINGS = [
  '/settings/google-calendar',
  '/settings/prescription-template',
  '/settings/catalog',
  '/settings/consultation-types',
  '/settings/whatsapp',
  '/settings/fiscal',
  '/settings/payment-providers',
  '/settings/antecedentes-template',
  '/settings/prescription-fields',
  '/settings/insurance-templates',
  '/settings/locations',
]

/**
 * Rutas de /informes permitidas al rol owner (spec owner-informes, mig 1000).
 * Lista exacta: sub-rutas de staff (p.ej. /informes/clinico) NO entran — solo
 * el hub y las 7 sub-rutas owner (creadas en Tasks 11-12).
 */
export const OWNER_INFORMES_ROUTES = [
  '/informes',
  '/informes/produccion', '/informes/estudios', '/informes/farmacia',
  '/informes/retencion', '/informes/ganancia',
  '/informes/financiero-owner', '/informes/inventario-owner',
]

export default defineNuxtRouteMiddleware(async (to) => {
  // Ruta que no corresponde a NINGUNA página registrada (404 genuino): dejar
  // pasar para que el afterEach nativo de Nuxt la maneje
  // (node_modules/nuxt/dist/pages/runtime/plugins/router.js: `to.matched.length
  // === 0` → showError(404) → error.vue). Antes, un anónimo pidiendo una ruta
  // inexistente caía en el "sin sesión → login" de abajo ANTES de que Nuxt
  // evaluara el 404, y veía el login en vez del boundary de error; un
  // autenticado sí lo veía porque no pega esa rama (smoke sweep 2026-09-06).
  // `to.matched` es la resolución real de vue-router (ya calculada antes de
  // que corran los middlewares globales) — no una lista hardcodeada — así que
  // rutas dinámicas (`/patients/[id]`) y los prefijos públicos de abajo siguen
  // intactos: SÍ matchean un archivo de página y llegan aquí con matched.length > 0.
  if (to.matched.length === 0) return

  // No aplicar en landing page ni rutas públicas
  if (to.path === '/') return
  if (PUBLIC_PREFIXES.some(p => to.path.startsWith(p))) return

  const user = useSupabaseUser()

  // Sin sesión → login
  if (!user.value) return navigateTo('/auth/login')

  const authStore  = useAuthStore()
  const permsStore = usePermissionsStore()
  const { hasRole, hasAnyRole } = useRoles()

  // Garantizar que el perfil está cargado.
  //
  // Un solo fallo NO cierra la sesión. Antes, cualquier excepción de
  // `fetchProfile()` mandaba a /auth/login: un 500 pasajero, un corte de red de
  // un segundo o un throttle bastaban para echar a alguien cuya sesión de Cognito
  // estaba perfectamente viva. Se reintenta una vez antes de rendirse; si el
  // segundo intento también falla, ahí sí al login (sin perfil no hay forma de
  // evaluar permisos, y seguir sería peor).
  if (!authStore.profile) {
    try {
      await authStore.fetchProfile()
    } catch {
      try {
        await new Promise(r => setTimeout(r, 400))
        await authStore.fetchProfile()
      } catch {
        return navigateTo('/auth/login')
      }
    }
  }

  // Agencias de cobranza (M-AG-003): las páginas de convenios/equipo existen solo
  // en el tenant agencia (equipo: solo admin); la página de convenios de la
  // clínica solo para admins de una clínica normal. Va ANTES del bloque
  // collection_manager, que permite /cobranza/* completo. Mismo predicado que el
  // sidebar (isAgencyHomeNavVisible, utils/agencyRoutes.ts): true para cualquier otra ruta.
  if (!isAgencyHomeNavVisible(to.path, { isAgencyTenant: authStore.isAgencyTenant, isAdmin: authStore.isAdmin })) {
    return navigateTo('/cobranza')
  }
  if (to.path.startsWith('/settings/agencias-cobranza')) {
    if (authStore.isAgencyTenant) return navigateTo('/cobranza')
    if (!authStore.isAdmin) return navigateTo('/settings')
  }
  // Part 5: una agencia suspendida solo abre «Plan y pagos» + ajustes personales.
  if (authStore.isAgencySuspended && !isAgencySuspendedRouteAllowed(to.path)) return navigateTo('/cobranza/plan')

  // Lab partner: confinado al portal del laboratorio (/portal/lab). No puede ver NADA de la clínica.
  if (hasRole('lab_partner')) {
    const allowed = to.path === '/portal/lab'
      || to.path.startsWith('/portal/lab/')
      || to.path.startsWith('/settings/profile')
      || to.path.startsWith('/settings/appearance')
    return allowed ? undefined : navigateTo('/portal/lab')
  }

  // Owner (dueño de cadena): confinado a /sucursales. Solo-lectura, sin
  // acceso a ningún módulo clínico. NO usar navigateTo('/dashboard') aquí —
  // owner no tiene permiso 'dashboard' y /dashboard cae en ROUTE_MODULE_MAP,
  // lo que produciría un loop de redirect infinito.
  if (hasRole('owner')) {
    // El sidebar filtra el único ítem del owner (/sucursales) por
    // canRead('sucursales'); sin cargar permisos aquí la nav queda vacía,
    // porque este branch retorna antes del fetchPermissions() genérico.
    if (!permsStore.loaded) {
      await permsStore.fetchPermissions()
    }
    const allowed = to.path === '/sucursales'
      || to.path.startsWith('/sucursales/')
      || OWNER_INFORMES_ROUTES.includes(to.path)
      || to.path.startsWith('/settings/profile')
      || to.path.startsWith('/settings/appearance')
    return allowed ? undefined : navigateTo('/sucursales')
  }

  // Gestora de cobranza de cirugías (R-COL-005): confinada a /cobranza,
  // /surgeries (lectura — las acciones de cobranza embebidas en el detalle
  // de cirugía están gateadas por el módulo 'collections', no 'surgeries')
  // (sin /ayuda: sus videos muestran módulos que el rol no tiene). Excluida de /dashboard aunque role_permissions SIGA en true ahí
  // (mig 1206 deja esa fila intacta a propósito — revocarla caería en la
  // rama "dashboard" de ROUTE_MODULE_MAP más abajo, que redirige a
  // /settings/profile en vez de /cobranza): el confinamiento real es este
  // bloque, no la fila de permisos — mismo patrón que `owner` arriba.
  if (hasRole('collection_manager') && !authStore.isAdmin) {
    if (!permsStore.loaded) {
      await permsStore.fetchPermissions()
    }
    // P3-R21: /settings/fiscal is her OWN fiscal data (M-PB-212) EXCEPT when
    // she is an agency member currently switched into a client clinic — there
    // stores/doctor_fiscal.ts#save() upserts with clinic_id = the CURRENT clinic:
    // her existing home row can't be moved (mig 050 doctor_fiscal_update USING
    // clinic_id = auth_clinic_id() hides it, the upsert just fails), but if she
    // has NO row in the client clinic it INSERTs a NEW personal row there
    // (doctor_fiscal_insert allows doctor_id = auth.uid()) — a stray emisor in
    // another tenant (final fix wave M-2; see isFiscalRouteBlockedForAgencyMember). The
    // layout only loads collection contexts onMounted, AFTER this middleware
    // resolves (layouts/dashboard.vue maybeLoadAgencyContexts), so a fresh
    // nav/deep-link to this one route needs it loaded here too — ctx.load()
    // populates the shared useState, so that onMounted call becomes a no-op
    // (agencyLoaded already true). Every other collection_manager route
    // (including a plain clinic's, never an agency member) skips this entirely.
    let blockedFiscal = false
    if (to.path === '/settings/fiscal') {
      const ctx = useCollectionContext()
      // load() never rethrows (useCollectionContext.ts catches internally,
      // sets error.value + resets contexts to []) — no try/catch needed here;
      // a failed load just leaves isInClientClinic false — fails open for
      // non-members (correct) AND for a real agency member inside a client
      // clinic whose load() failed (accepted: UX guard only; a DB-level guard
      // on doctor_fiscal_config INSERT for agency members outside home is a
      // follow-up — final fix wave M-2).
      if (!ctx.loaded.value) await ctx.load()
      blockedFiscal = isFiscalRouteBlockedForAgencyMember(to.path, ctx.isInClientClinic.value)
    }
    // M-SX-064 (mig 1270): she captures surgeries from /cobranza («Nueva cirugía» →
    // create_managed_surgery). /surgeries/new saves through create_surgery, which she
    // can't call (R-DOC-004) — a deep link there sends her where capture actually works,
    // unless she also holds a doctor role.
    if (to.path === '/surgeries/new' && !hasRole('doctor') && !hasRole('assistant_doctor')) {
      return navigateTo('/cobranza')
    }
    const allowed = to.path === '/cobranza'
      || to.path.startsWith('/cobranza/')
      || to.path === '/surgeries'
      || to.path.startsWith('/surgeries/')
      || to.path.startsWith('/settings/profile')
      || to.path.startsWith('/settings/appearance')
      // M-PB-212 / mig 1213: sus propios datos fiscales + CSD (emisor de la
      // factura de su comisión). RLS: solo su propia fila (mig 050/1212).
      || (to.path === '/settings/fiscal' && !blockedFiscal)
    return allowed ? undefined : navigateTo('/cobranza')
  }

  // Agencia de cobranza (M-AG-002, mig 1230): la administradora del tenant agencia no
  // tiene pacientes ni módulos clínicos — confinada a /cobranza, detalle de cirugía y
  // su configuración. Mismo patrón que `owner`. Dentro de una clínica cliente su
  // perfil es collection_manager en un tenant `clinic`, así que cae en el bloque de
  // arriba, no en este.
  if (authStore.isAgencyTenant && authStore.isAdmin) {
    if (!permsStore.loaded) {
      await permsStore.fetchPermissions()
    }
    return isAgencyAdminAllowed(to.path) ? undefined : navigateTo('/cobranza')
  }

  // El namespace /portal es exclusivo de portales de partner (lab, etc.).
  // Un usuario de clínica que llegue aquí no debe ver el portal con sus propios datos.
  if (to.path.startsWith('/portal/')) {
    return navigateTo(authStore.isSuperadmin ? '/admin' : '/dashboard')
  }

  // Garantizar que los permisos están cargados
  if (!permsStore.loaded) {
    await permsStore.fetchPermissions()
  }

  // Superadmin platform-only: redirige a /admin a menos que esté impersonando
  // una clínica. Sin impersonation, el sidebar / RLS no tienen tenant scope.
  if (authStore.isSuperadmin) {
    const impersonating = !!(user.value as any)?.app_metadata?.impersonated_clinic_id
    if (!impersonating && !to.path.startsWith('/admin') && !to.path.startsWith('/onboarding')) {
      return navigateTo('/admin')
    }
  }

  // Solicitudes: visible para clínicas con web intake O con un laboratorio asignado
  // (referidos). canSeeSolicitudes es la ÚNICA regla de la ruta — no exige el permiso
  // de módulo appointment_requests, de modo que admin / doctor / recepción de una
  // clínica con lab (y la impersonación de superadmin, cuyo perfil puede ser doctor)
  // puedan abrir la bandeja. Returns early para no caer en ROUTE_MODULE_MAP.
  if (to.path.startsWith('/solicitudes')) {
    return authStore.canSeeSolicitudes ? undefined : navigateTo('/dashboard')
  }

  // Admin puro (sin rol clínico): bloqueado de recetas
  if (hasRole('admin') && !authStore.isClinicDoctor && to.path.startsWith('/prescriptions')) {
    return navigateTo('/dashboard')
  }

  // Admin puro (sin rol clínico): bloqueado de configuración exclusiva de doctor
  if (hasRole('admin') && !authStore.isClinicDoctor && to.path.startsWith('/settings/prescription-template')) {
    return navigateTo('/dashboard')
  }

  // Admin tiene acceso a todo lo demás
  if (hasRole('admin')) return

  // Todos los roles: acceso a settings personales (ej. /settings/appearance)
  if (ALL_USER_SETTINGS.some(p => to.path.startsWith(p))) return

  // Receptionist: gestión de línea WhatsApp (conectar/desconectar)
  if (hasRole('receptionist') && to.path.startsWith('/settings/whatsapp')) return

  // Doctores y asistentes de doctor: acceso a /settings raíz y sus sub-páginas personales
  if (authStore.isClinicDoctor && to.path.startsWith('/settings')) {
    const allowed = to.path === '/settings'
      || DOCTOR_SETTINGS.some(p => to.path.startsWith(p))
    if (!allowed) return navigateTo('/dashboard')
    return
  }

  // Rutas exclusivas de doctores y asistentes de doctor
  if (to.path.startsWith('/mis-archivos') && !authStore.isClinicDoctor) {
    return navigateTo('/dashboard')
  }

  // /descuentos: solo doctoras (primario o additional_role) — los demás roles
  // no tienen acceso al catálogo de descuentos propios (M-PB-110, M-PB-151).
  if (to.path.startsWith('/descuentos') && !authStore.isDoctor) {
    return navigateTo('/dashboard')
  }

  // /agenda/configuracion: doctor (dueño de su horario), admin, y recepción/
  // administrativo — estos últimos editan el horario de cualquier médico vía
  // selector + RLS (mig 707). Enfermera sigue sin acceso de escritura. (M-AE-175)
  if (
    to.path.startsWith('/agenda/configuracion')
    && !authStore.isClinicDoctor
    && !hasRole('admin')
    && !hasAnyRole(['receptionist', 'administrative', 'assistant'])
  ) {
    return navigateTo('/agenda')
  }

  // Portal del paciente (/patients/[id]/portal): solo roles con gestión de portal.
  // La enfermera tiene acceso READ a /patients pero no puede administrar tokens de compartición.
  // Esta comprobación es UX (capa B); la capa real es RLS en patient_share_tokens (mig 590).
  if (/^\/patients\/[^/]+\/portal(\/|$)/.test(to.path)) {
    const canManagePortal = hasAnyRole([
      'doctor', 'admin', 'receptionist', 'assistant_doctor',
      'accountant', 'collection_manager', 'administrative',
    ])
    if (!canManagePortal) return navigateTo('/dashboard')
  }

  // POS: recepción siempre tiene acceso (no configurable). Doctor / enfermera y
  // demás roles caen en ROUTE_MODULE_MAP → gateados por el permiso 'pos'
  // (habilitable por el administrador). Returns early para no exigir el permiso.
  if (to.path.startsWith('/pos') && hasRole('receptionist')) return

  // Encontrar el módulo que corresponde a esta ruta
  const match = ROUTE_MODULE_MAP.find(
    r => to.path === r.prefix || to.path.startsWith(r.prefix + '/')
  )
  if (!match) return

  // Sin permiso de lectura → redirigir al dashboard
  if (!permsStore.can(match.module, 'read')) {
    // Si el módulo negado ES dashboard, redirigir a /dashboard sería un loop
    // infinito (error 500 en app init) — p.ej. un rol sin fila 'dashboard' en
    // role_permissions. Safe harbor: settings personales (ALL_USER_SETTINGS).
    if (match.module === 'dashboard') {
      return navigateTo('/settings/profile')
    }
    return navigateTo('/dashboard')
  }
})
